import { FormEvent, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Heading } from '@spark-web/heading';
import { ArrowRightIcon, CheckIcon, XIcon } from '@spark-web/icon';
import { Inline } from '@spark-web/inline';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import type { SparkTheme } from '@spark-web/theme';
import BrighteMarkIcon from '../../../assets/brighte-mark.svg?react';
import { useAssistant } from './AssistantProvider';
import { MarkdownLite } from './MarkdownLite';
import {
  ASSISTANT_DISCLAIMER,
  ASSISTANT_RATING_ANCHORS,
  ASSISTANT_RATING_PROMPT,
  ASSISTANT_RATING_SCALE,
  ASSISTANT_RATING_THANKS,
  QUICK_ACTIONS,
} from './constants';
import { useDialogDismissShield } from './useDialogDismissShield';

/** The input stops growing here and scrolls instead. A line count, not a design value. */
const MAX_INPUT_ROWS = 5;
/** Bubble width from the Figma frames — the panel's content width less an opposite-side gutter. */
const BUBBLE_MAX_WIDTH = 306;

const panel = (theme: SparkTheme) => css`
  position: fixed;
  /* Stay clickable while a Radix modal locks pointer events on the body. */
  pointer-events: auto;
  /* Above Spark's modal elevation (300) so the panel remains usable when
     opened from inside a dialog like the upload modal. */
  z-index: 400;
  background: ${theme.color.background.surface};
  border: ${theme.border.width.standard}px solid ${theme.border.color.standard};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  right: ${theme.spacing.xlarge}px;
  bottom: ${theme.spacing.xlarge}px;
  width: 360px;
  height: 620px;
  max-height: calc(100vh - ${theme.spacing.xlarge * 2}px);
  border-radius: ${theme.border.radius.large}px;
  box-shadow: ${theme.shadow.large};
  @media (max-width: 599px) {
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 85vh;
    border-radius: ${theme.border.radius.large}px ${theme.border.radius.large}px 0 0;
  }
`;

const thread = css`
  flex-grow: 1;
  overflow-y: auto;
  /* Without this the flex item keeps its content height (min-height defaults to
     auto), so a long thread grows the panel instead of scrolling inside it. The
     panel then scrolls itself on scrollIntoView — overflow:hidden still scrolls
     programmatically — carrying the header off the top. */
  min-height: 0;
`;

const bubble = (own: boolean, theme: SparkTheme) => css`
  box-sizing: border-box;
  max-width: ${BUBBLE_MAX_WIDTH}px;
  align-self: ${own ? 'flex-end' : 'flex-start'};
  background: ${own ? theme.color.background.primaryLow : theme.color.background.infoMuted};
  border-radius: ${theme.border.radius.large}px ${theme.border.radius.large}px
    ${own ? theme.border.radius.small : theme.border.radius.large}px
    ${own ? theme.border.radius.large : theme.border.radius.small}px;
  padding: ${theme.spacing.medium}px;
`;

/**
 * Standard screen-reader-only pattern. These are a11y mechanics rather than
 * design values, so they are not expressed as tokens.
 */
const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/** Matches the Input Field tokens the design uses for the composer. */
const inputShell = (theme: SparkTheme) => css`
  box-sizing: border-box;
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing.small}px;
  min-height: ${theme.sizing.medium}px;
  background: ${theme.color.background.input};
  border: ${theme.border.width.standard}px solid ${theme.border.color.standard};
  border-radius: ${theme.border.radius.small}px;
  box-shadow: ${theme.shadow.small};
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  /* The textarea clears its own outline, so the focus indicator lives on the shell. Mirrors
     Spark's useFocusRing: a ring of border.width.large in background.primary at 30% alpha
     (the token is a 6-digit hex, so the alpha is appended as the 4th channel). */
  &:focus-within {
    box-shadow:
      ${theme.shadow.small},
      0 0 0 ${theme.border.width.large}px ${theme.color.background.primary}4d;
  }
`;

const inputFieldWrap = css`
  position: relative;
  flex: 1;
  display: flex;
`;

// DS-EXCEPTION: AssistantComposerTextarea
// Why: @spark-web/text-area lacks onKeyDown/onScroll/rows and auto-grow
// Approved: Tyren Temple, 2026-09-30
// Exit: replace with Spark TextArea once it supports auto-grow and key handlers
const textarea = (theme: SparkTheme) => css`
  box-sizing: border-box;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: ${theme.typography.fontFamily.sans.name};
  /* Matches the thread's Text size="small", for typed text and the placeholder alike.
     The panel only renders at ASSISTANT_MIN_VIEWPORT_PX and above, where Spark resolves
     Text to its tablet values, so these read tablet rather than mobile. Typography
     tokens are preformatted strings ("0.9375rem"), unlike the numeric spacing tokens
     above, so appending px here would void the declaration. */
  font-size: ${theme.typography.text.small.tablet.fontSize};
  line-height: ${theme.typography.text.small.tablet.lineHeight};
  color: ${theme.color.foreground.neutral};
  padding: 0;
  &::placeholder {
    color: ${theme.color.foreground.placeholder};
  }
`;

/** Decorative cue that there is more text scrolled out of view above. */
const inputFade = (theme: SparkTheme) => css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${theme.spacing.xlarge}px;
  /* Holds solid for the first half before fading, so the clipped line above
     reads as cut off rather than just slightly dimmed. */
  background: linear-gradient(
    to bottom,
    ${theme.color.background.input} 0%,
    ${theme.color.background.input} 45%,
    transparent 100%
  );
  pointer-events: none;
`;

export function AssistantPanel() {
  const {
    stepKey,
    subState,
    close,
    messages,
    send: sendMessage,
    isLoading,
    ratingStage,
    submitRating,
    skipRating,
  } = useAssistant();
  const theme = useTheme();

  const [draft, setDraft] = useState('');
  const [isInputScrolled, setIsInputScrolled] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const lastReplyRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Esc / the header × / an outside dismiss all skip the rating prompt
  // immediately rather than re-triggering it — it is already showing.
  // useCallback keeps this stable across renders: useDialogDismissShield
  // re-attaches its body listeners whenever this identity changes.
  const dismiss = useCallback(
    () => (ratingStage !== 'none' ? skipRating() : close()),
    [ratingStage, skipRating, close],
  );
  useDialogDismissShield(rootRef, dismiss);

  const canSend = draft.trim().length > 0 && !isLoading;

  const submit = () => {
    if (!canSend) return;
    if (sendMessage(draft)) setDraft('');
  };

  const send = (content: string) => {
    if (sendMessage(content)) setDraft('');
  };

  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    // Scroll the thread itself rather than calling scrollIntoView: that walks up
    // and scrolls every scrollable ancestor, and the panel is one of them
    // (overflow:hidden still scrolls programmatically), which dragged the header
    // and composer out of place on long threads.
    const container = threadRef.current;
    if (!container) return;
    // A reply can be far taller than the panel, so scrolling to the end of the
    // thread lands the customer on its last line with the answer above them.
    // Put the TOP of the newest answer in view instead and let them read down;
    // their own message still scrolls to the end, which is where the reply
    // will appear.
    if (lastMessage?.role === 'assistant' && lastReplyRef.current) {
      const offset = lastReplyRef.current.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTop += offset;
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [messages.length, lastMessage?.role]);

  // Put the keyboard where the customer needs it as soon as the panel opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Grow with the content up to MAX_INPUT_ROWS, then scroll inside the field.
  // Line height is read back from the element so the cap follows the type
  // tokens rather than a duplicated pixel value.
  const resizeInput = useCallback(() => {
    const element = inputRef.current;
    if (!element) return;
    // Growing the composer shrinks the thread's viewport. Its scroll offset does
    // not move with it, so without this the newest messages slide out of sight
    // behind the composer. Hold the distance to the bottom steady instead, which
    // walks the thread up as the composer grows and back down as it shrinks.
    const container = threadRef.current;
    const distanceFromBottom = container ? container.scrollHeight - container.scrollTop - container.clientHeight : 0;

    element.style.height = 'auto';
    const { lineHeight, paddingTop, paddingBottom } = getComputedStyle(element);
    const maxHeight = parseFloat(lineHeight) * MAX_INPUT_ROWS + parseFloat(paddingTop) + parseFloat(paddingBottom);
    element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
    setIsInputScrolled(element.scrollTop > 0);

    if (container) {
      // Reading clientHeight here reflows first, so this is the post-resize height.
      container.scrollTop = container.scrollHeight - container.clientHeight - distanceFromBottom;
    }
  }, []);

  // Layout effect so the field is never painted at the wrong height, including
  // the shrink back to one line after sending.
  useLayoutEffect(resizeInput, [draft, resizeInput]);

  const onPanelKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      // Close the assistant only — never the dialog underneath it.
      event.stopPropagation();
      dismiss();
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter confirms a candidate word on a Japanese, Chinese or Korean keyboard — submitting
    // there would send half a sentence.
    if (event.nativeEvent.isComposing) return;
    if (event.key !== 'Enter' || event.shiftKey) return;
    // Touch keyboards have no Shift, so return must insert a newline there and
    // the send button is the only way to submit.
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    event.preventDefault();
    submit();
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  // Starter suggestions only: once the customer has asked anything (typed,
  // via a chip, or through an error hand-off) the chips get out of the way.
  const conversationStarted = messages.some(message => message.role === 'user');
  // Sub-state first — it tracks what is on screen — then the step-level default for anything
  // the step has not reported a sub-state for yet.
  const quickActions = conversationStarted
    ? []
    : (subState && QUICK_ACTIONS[subState]) || (stepKey && QUICK_ACTIONS[stepKey]) || [];

  return (
    <section
      ref={rootRef}
      className={panel(theme)}
      aria-label="Brighte AI Assistant"
      data-testid="assistant-panel"
      onKeyDown={onPanelKeyDown}
    >
      <Box display="flex" alignItems="center" gap="medium" padding="large" borderBottom="standard">
        {/* DS-EXCEPTION: AssistantLogoGreen
            Why: Figma decorative/green/medium (#00C28C) has no Spark token; nearest foreground.primary is #00E0A3
            Approved: Tyren Temple, 2026-09-30
            Exit: swap for a Spark token when one ships */}
        <span style={{ color: '#00c28c', display: 'inline-flex' }}>
          <BrighteMarkIcon width={24} height={24} />
        </span>
        <Box flex={1}>
          <Heading level="4">Brighte AI Assistant</Heading>
        </Box>
        <Button label="Close assistant" tone="neutral" prominence="none" size="medium" onClick={dismiss}>
          <XIcon size="xxsmall" />
        </Button>
      </Box>

      <Box ref={threadRef} className={thread} paddingTop="large" paddingX="large" role="log" aria-live="polite">
        <Stack gap="large">
          {messages.map((message, index) => (
            <div
              key={message.id}
              ref={index === messages.length - 1 ? lastReplyRef : undefined}
              className={bubble(message.role === 'user', theme)}
            >
              <span className={visuallyHidden}>
                {message.role === 'user' ? 'You said:' : 'Brighte AI Assistant said:'}
              </span>
              <MarkdownLite content={message.content} />
            </div>
          ))}
          {isLoading ? (
            <Inline gap="small" alignY="center">
              <Spinner size="xxsmall" tone="primary" />
              <Text size="xsmall" tone="muted">
                Thinking…
              </Text>
            </Inline>
          ) : null}
        </Stack>
      </Box>

      {quickActions.length ? (
        <Box paddingX="large" paddingTop="large">
          <Stack gap="small" align="right">
            {quickActions.map(action => (
              <Button key={action} size="small" tone="neutral" prominence="high" onClick={() => send(action)}>
                {action}
              </Button>
            ))}
          </Stack>
        </Box>
      ) : null}

      {ratingStage === 'prompt' ? (
        <Box borderTop="standard" padding="medium" data-testid="assistant-rating-prompt">
          <Stack gap="small">
            <Box display="flex" alignItems="center" gap="small">
              <Text weight="semibold" size="small">
                {ASSISTANT_RATING_PROMPT}
              </Text>
              <Box flex={1} />
              <Button tone="neutral" prominence="none" onClick={skipRating}>
                Skip
              </Button>
            </Box>
            <Box display="flex" gap="small" role="group" aria-label="Rate this conversation from 1 to 5">
              {Array.from({ length: ASSISTANT_RATING_SCALE }, (_, index) => index + 1).map(value => (
                <Box key={value} flex={1}>
                  <Button tone="neutral" prominence="high" css={{ width: '100%' }} onClick={() => submitRating(value)}>
                    {value}
                  </Button>
                </Box>
              ))}
            </Box>
            <Box display="flex" justifyContent="spaceBetween">
              <Text size="xsmall" tone="muted">
                {ASSISTANT_RATING_ANCHORS[0]}
              </Text>
              <Text size="xsmall" tone="muted">
                {ASSISTANT_RATING_ANCHORS[1]}
              </Text>
            </Box>
          </Stack>
        </Box>
      ) : ratingStage === 'thanks' ? (
        <Box borderTop="standard" padding="medium" data-testid="assistant-rating-thanks">
          <Box display="flex" alignItems="center" gap="small">
            <span style={{ color: '#00c28c', display: 'inline-flex' }}>
              <CheckIcon size="xsmall" />
            </span>
            <Text size="small">{ASSISTANT_RATING_THANKS}</Text>
          </Box>
        </Box>
      ) : (
        <Box padding="large">
          <Stack gap="medium">
            <form onSubmit={onSubmit}>
              <Box display="flex" gap="small" alignItems="end">
                <Box flex={1}>
                  <div className={inputShell(theme)}>
                    <div className={inputFieldWrap}>
                      <textarea
                        ref={inputRef}
                        className={textarea(theme)}
                        rows={1}
                        aria-label="Ask a question"
                        placeholder="Ask a question..."
                        value={draft}
                        onChange={event => setDraft(event.currentTarget.value.slice(0, 2000))}
                        onScroll={event => setIsInputScrolled(event.currentTarget.scrollTop > 0)}
                        onKeyDown={onInputKeyDown}
                      />
                      {isInputScrolled ? <div aria-hidden="true" className={inputFade(theme)} /> : null}
                    </div>
                  </div>
                </Box>
                <Button
                  type="submit"
                  label="Send message"
                  tone="primary"
                  prominence="high"
                  size="medium"
                  disabled={!canSend}
                >
                  <ArrowRightIcon size="xxsmall" />
                </Button>
              </Box>
            </form>
            <Text size="xsmall" tone="placeholder" align="center">
              {ASSISTANT_DISCLAIMER}
            </Text>
          </Stack>
        </Box>
      )}
    </section>
  );
}
